// src/cockpit4/components/ContentPreCompareModal.tsx
// ============================================
// 生成前对比选择弹窗（v5.2）
//
// 用户诉求：对比不只在发布后，更要放在"生成之前"——
// 先生成「约束版」（完整链路）与「裸写版」（无约束）两个草稿，
// 前端即时评分对比 → 用户选择最优版 → 正式落库发布。
// 这样产出的内容起点更高（白皮书决策原则：系统出方案 → 人选最优）。
// ============================================

import { useState } from "react";
import { createPortal } from "react-dom";
import { X, Loader2, Scale, Sparkles, Database, Check, AlertTriangle } from "lucide-react";
// v5.2 修复：必须直接 import RuntimeManager/RuntimeName（不能从 window 取，window 上没有）
import RuntimeManager from "../../runtime/RuntimeManager";
import { RuntimeName } from "../../types/runtime";

interface CompareResult {
  topic: string;
  platform: string;
  constrained: { content: string; label: string };
  raw: { content: string; label: string };
}

interface Props {
  open: boolean;
  topic: string;
  platformLabel: string;
  /** v5.2 平台 id（wechat/douyin_enterprise/xiaohongshu/bilibili...）用于 compare_versions */
  platformId: string;
  onClose: () => void;
  onSelect: (content: string, source: "constrained" | "raw") => void;
}

/** 轻量评分（6 维，与 ContentCompareLab 一致） */
function quickScore(content: string, platform: string): { score: number; label: string; wins: number } {
  const text = content || "";
  const experienceSignals = ["我们", "踩坑", "踩过", "失败", "复盘", "真实", "早期", "后来", "决策留痕", "采纳率", "准确率", "真相源", "飞轮", "部署"].filter((w) => text.includes(w)).length;
  const hasNumbers = (text.match(/\d+(\.\d+)?%|个|家|篇|条|周/g) || []).length > 0;
  const hasBrand = /小舍|XEAOS|李师兄/.test(text);
  const eaat = Math.round(Math.min(100, 30 + experienceSignals * 8) * 0.5 + (hasNumbers ? 25 : 0) + (hasBrand ? 25 : 0));

  const specMap: Record<string, string[]> = {
    douyin_enterprise: ["大错特错", "别", "你", "关注"],
    douyin_personal: ["我", "我们", "踩", "真实"],
    shipinhao: ["信任", "建议", "我们", "验证"],
    wechat: ["为什么", "怎么", "这篇文章", "结论"],
    xiaohongshu: ["清单", "干货", "建议收藏", "步骤"],
    bilibili: ["本章", "讲解", "深入", "原理"],
  };
  const hooks = (specMap[platform] || []).filter((h) => text.includes(h)).length;
  const platformScore = Math.round(Math.min(100, 30 + hooks * 15));

  const head = text.slice(0, 120);
  const hook = Math.round(Math.min(100, 30 + (/大错特错|你知道吗|千万别|踩过|为什么|怎么|居然/.test(head) ? 35 : 0) + (/[0-9?？]/.test(head) ? 15 : 0)));

  const paragraphs = text.split(/\n\n+/).filter((p) => p.trim());
  const structure = Math.round(Math.min(100, (paragraphs.length >= 3 ? 35 : 10) + (/^#{1,3}\s/m.test(text) ? 30 : 0) + (/^[-*]\s/m.test(text) ? 20 : 0) + (text.length >= 200 ? 15 : 0)));

  const sensitive = ["第一", "绝对", "保证", "100%", "免费", "点击领取", "稳赚", "暴富"].filter((w) => text.includes(w));
  const compliance = Math.max(0, 100 - sensitive.length * 20);

  const score = Math.round((eaat + platformScore + hook + structure + compliance) / 5);
  return { score, label: `${score} 分`, wins: [eaat, platformScore, hook, structure, compliance].filter((v) => v >= 60).length };
}

const PLATFORM_COLORS: Record<string, string> = {
  wechat: "#07C160", douyin_enterprise: "#000000", douyin_personal: "#222222",
  xiaohongshu: "#FF2442", shipinhao: "#FA7298", bilibili: "#FB7299", zhihu: "#0084FF",
};

export default function ContentPreCompareModal({ open, topic, platformLabel, platformId, onClose, onSelect }: Props) {
  const [result, setResult] = useState<CompareResult | null>(null);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [preview, setPreview] = useState<"constrained" | "raw" | null>(null);

  if (!open) return null;

  const runCompare = async () => {
    setRunning(true);
    setError(null);
    setResult(null);
    try {
      // v5.2 修复：直接 import 的 RuntimeManager/RuntimeName（不能用 window，window 上没有 → "Runtime undefined not found"）
      const resp = await RuntimeManager.execute(
        RuntimeName.MediaStudio,
        "compare_versions",
        { platform: platformId, topic, brand: "xeaos" }
      );
      // v5.2.2 修复：必须用 unwrapResponse 解包双层 data！
      // RuntimeManager.execute 返回 { success, data: MediaStudioResult }，
      // MediaStudioResult.data 才是 { constrained, raw }。直接取 resp.data 会拿到包装层 → constrained 为 undefined → 误报"未返回内容"
      const inner = RuntimeManager.unwrapResponse<any>(resp);
      const data = inner || { topic, platform: platformId, constrained: { content: "", label: "约束版" }, raw: { content: "", label: "裸写版" } };
      const constrainedText = data.constrained?.content || "";
      const rawText = data.raw?.content || "";
      if (!constrainedText && !rawText) {
        // v5.2.1 修复：不再用演示占位文本（会导致假内容落库！），明确报错让用户重试
        setError("对比生成未返回内容（LLM 调用可能失败），请稍后重试或检查 DeepSeek 配置");
        setResult(null);
      } else {
        setResult({ topic, platform: data.platform || platformId, constrained: { content: constrainedText, label: "约束版（完整链路）" }, raw: { content: rawText, label: "裸写版（无约束）" } });
      }
    } catch (err: any) {
      setError(err?.message || String(err));
    } finally {
      setRunning(false);
    }
  };

  const cScore = result ? quickScore(result.constrained.content, result.platform) : null;
  const rScore = result ? quickScore(result.raw.content, result.platform) : null;

  return (
    <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-md flex items-center justify-center p-4">
      <div className="w-full max-w-4xl max-h-[85vh] bg-[#0a0e1a] rounded-xl border border-purple-500/20 overflow-hidden flex flex-col">
        {/* 头部 */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-white/[0.06]">
          <div className="flex items-center gap-2">
            <Scale size={14} className="text-purple-400" />
            <h3 className="text-[13px] font-medium text-white">生成前对比 · {topic.slice(0, 24)}</h3>
            <span className="text-[9px] font-mono px-1.5 py-0.5 rounded" style={{ background: `${PLATFORM_COLORS[result?.platform || "wechat"]}20`, color: PLATFORM_COLORS[result?.platform || "wechat"] }}>
              {platformLabel}
            </span>
          </div>
          <div className="flex items-center gap-2">
            {!result && (
              <button onClick={runCompare} disabled={running} className="flex items-center gap-1 px-3 py-1 text-[10px] font-mono text-white bg-purple-600/80 hover:bg-purple-600 rounded transition-colors disabled:opacity-40">
                {running ? <Loader2 size={10} className="animate-spin" /> : <Sparkles size={10} />}
                {running ? "对比生成中（两版并行）..." : "开始对比生成"}
              </button>
            )}
            <button onClick={onClose} className="p-1 text-gray-400 hover:text-white"><X size={14} /></button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-4">
          {error && <div className="mb-3 text-[10px] font-mono text-red-400 px-2 py-1.5 rounded bg-red-500/10 border border-red-500/20">{error}</div>}

          {!result && !running && (
            <div className="text-center py-10">
              <div className="text-[11px] font-mono text-gray-500 mb-2">
                生成前先对比：同时产出「约束版」（Obsidian 经验+平台规格约束）与「裸写版」（直接 LLM 无约束）
              </div>
              <div className="text-[9px] font-mono text-gray-600">对比选择后再正式落库发布，内容起点更高</div>
            </div>
          )}

          {running && !result && (
            <div className="flex flex-col items-center justify-center py-12">
              <Loader2 size={20} className="animate-spin text-purple-400 mb-3" />
              <div className="text-[10px] font-mono text-gray-500">正在并行生成两个版本并评分...</div>
            </div>
          )}

          {result && cScore && rScore && (
            <>
              {/* 双版本卡片 */}
              <div className="grid grid-cols-2 gap-3 mb-3">
                {/* 约束版 */}
                <div className={`rounded-lg border p-3 ${cScore.score >= rScore.score ? "border-teal-500/40 bg-teal-500/[0.04]" : "border-white/[0.08] bg-white/[0.02]"}`}>
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-1.5">
                      <Database size={11} className="text-teal-400" />
                      <span className="text-[10px] font-mono text-teal-300">约束版</span>
                      {cScore.score >= rScore.score && <span className="text-[8px] font-mono px-1 py-0.5 rounded bg-teal-500/20 text-teal-300">推荐</span>}
                    </div>
                    <span className="text-[13px] font-medium text-teal-300">{cScore.score}<span className="text-[9px] text-teal-500">分</span></span>
                  </div>
                  <div className="text-[9px] font-mono text-gray-500 mb-2">{result.constrained.label}</div>
                  <button
                    onClick={() => setPreview("constrained")}
                    className="w-full text-left text-[9px] font-mono text-gray-400 bg-white/[0.02] border border-white/[0.06] rounded p-2 max-h-[90px] overflow-hidden leading-relaxed"
                  >
                    {result.constrained.content.slice(0, 150)}...
                  </button>
                  <button onClick={() => setPreview("constrained")} className="mt-2 w-full px-2 py-1 text-[9px] font-mono text-teal-300 bg-teal-500/10 hover:bg-teal-500/20 rounded">预览全文</button>
                  <button
                    onClick={() => { onSelect(result.constrained.content, "constrained"); onClose(); }}
                    className="mt-1 w-full flex items-center justify-center gap-1 px-2 py-1.5 text-[10px] font-mono text-white bg-teal-600/80 hover:bg-teal-600 rounded"
                  >
                    <Check size={10} /> 选用此版
                  </button>
                </div>

                {/* 裸写版 */}
                <div className={`rounded-lg border p-3 ${rScore.score > cScore.score ? "border-coral-500/40 bg-coral-500/[0.04]" : "border-white/[0.08] bg-white/[0.02]"}`}>
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-1.5">
                      <AlertTriangle size={11} className="text-coral-400" />
                      <span className="text-[10px] font-mono text-coral-300">裸写版</span>
                      {rScore.score > cScore.score && <span className="text-[8px] font-mono px-1 py-0.5 rounded bg-coral-500/20 text-coral-300">推荐</span>}
                    </div>
                    <span className="text-[13px] font-medium text-coral-300">{rScore.score}<span className="text-[9px] text-coral-500">分</span></span>
                  </div>
                  <div className="text-[9px] font-mono text-gray-500 mb-2">{result.raw.label}</div>
                  <button
                    onClick={() => setPreview("raw")}
                    className="w-full text-left text-[9px] font-mono text-gray-400 bg-white/[0.02] border border-white/[0.06] rounded p-2 max-h-[90px] overflow-hidden leading-relaxed"
                  >
                    {result.raw.content.slice(0, 150)}...
                  </button>
                  <button onClick={() => setPreview("raw")} className="mt-2 w-full px-2 py-1 text-[9px] font-mono text-coral-300 bg-coral-500/10 hover:bg-coral-500/20 rounded">预览全文</button>
                  <button
                    onClick={() => { onSelect(result.raw.content, "raw"); onClose(); }}
                    className="mt-1 w-full flex items-center justify-center gap-1 px-2 py-1.5 text-[10px] font-mono text-white bg-coral-600/80 hover:bg-coral-600 rounded"
                  >
                    <Check size={10} /> 选用此版
                  </button>
                </div>
              </div>

              {/* 评分说明 */}
              <div className="rounded-lg border border-white/[0.06] bg-white/[0.02] p-2.5">
                <div className="text-[9px] font-mono text-gray-500 leading-relaxed">
                  <span className="text-teal-400">约束版</span> 优势：GEO 可信度（真实经验/可溯源数据/品牌身份）· 平台规格适配 ·
                  <span className="text-coral-400">裸写版</span> 通常钩子/情绪更强但缺乏真实支撑（大模型引用意愿低）。
                  评分 = GEO可信度 + 平台适配 + 钩子 + 结构 + 合规（5 维平均）。
                </div>
              </div>
            </>
          )}
        </div>

        {/* 预览弹层 */}
        {preview && result && (
          <div className="fixed inset-0 z-[60] bg-black/90 backdrop-blur-md flex items-center justify-center p-6" onClick={() => setPreview(null)}>
            <div className="w-full max-w-2xl max-h-[80vh] bg-[#0a0e1a] rounded-xl border border-white/[0.1] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
              <div className="flex items-center justify-between px-4 py-2.5 border-b border-white/[0.06]">
                <span className="text-[11px] font-medium text-white">{preview === "constrained" ? "约束版全文" : "裸写版全文"}</span>
                <button onClick={() => setPreview(null)} className="p-1 text-gray-400 hover:text-white"><X size={13} /></button>
              </div>
              <div className="flex-1 overflow-y-auto p-4 text-[11px] text-gray-300 whitespace-pre-wrap leading-relaxed font-mono">
                {preview === "constrained" ? result.constrained.content : result.raw.content}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
